/**
 * useSocketReconnectResync — refaz o que o socket perdeu numa queda de rede.
 *
 * Os eventos emitidos enquanto o socket estava fora (mensagem nova, contador,
 * lista de conversas) NÃO são reenviados na reconexão — o backend não usa
 * `connectionStateRecovery`. Sem isto, a conversa aberta ficava com a mensagem
 * faltando até o próximo `refetchOnWindowFocus`, que só dispara em
 * `visibilitychange` (React Query 5): janela visível mas sem foco NUNCA
 * dispara. Enquanto isso o contador seguia certo (tem `refetchInterval`) e o
 * push chegava pelo OneSignal — o "toast e contador atualizam, o balão não
 * aparece" do relato de 2026-08-29.
 *
 * Par desta mudança: `SocketContext` deixou de limitar `reconnectionAttempts`
 * (com 5 tentativas o socket desistia para sempre depois de ~20s).
 *
 * `reconnect` é evento do Manager (`socket.io`), não do Socket — o `connect`
 * do Socket também dispara na PRIMEIRA conexão, quando não há nada a refazer.
 * Só as queries ATIVAS refazem (`refetchType` default): a conversa que está na
 * tela, não todas as que já passaram pelo cache.
 *
 * Montado uma vez, em `GlobalSocketListeners`.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useSocket } from '@/contexts/SocketContext';

export const useSocketReconnectResync = (): void => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;

    const handleReconnect = (): void => {
      void queryClient.invalidateQueries({ queryKey: ['messages', 'infinite'], exact: false });
      void queryClient.invalidateQueries({ queryKey: ['conversation'], exact: false });
      // Lista + contadores por tipo (`['conversations', 'unread-count', tipo]`)
      void queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
      void queryClient.invalidateQueries({ queryKey: ['unread-count'] });
      void queryClient.invalidateQueries({ queryKey: ['email-unread-count'] });
    };

    socket.io.on('reconnect', handleReconnect);
    return () => {
      socket.io.off('reconnect', handleReconnect);
    };
  }, [socket, queryClient]);
};
